import { LitElement, html, unsafeCSS, CSSResult } from 'lit';
import styles from './skeleton-loader.scss?inline';
import { property } from 'lit/decorators.js';
import { literal } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import slotStyles from './skeleton-loader.slot.scss?inline';
import { appendStylesheet, c11nElement } from '../core';

export type SkeletonLoaderTextHeight =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl';

export type SkeletonLoaderVariant = 'shape' | 'text-block';

@c11nElement()
export class SkeletonLoaderComponent extends LitElement {
  static selector = `${C11nPrefix}-skeleton-loader`;
  static literal = literal`${C11nPrefixLiteral}-skeleton-loader`;
  static override styles: CSSResult = unsafeCSS(styles);

  // Some attributes (variant,radius) were being stripped in Stencil apps,
  // but we found a simple workaround. If using @property
  // we may need to differentiate the name from the attribute
  // https://github.com/ionic-team/stencil/issues/2703
  @property({ attribute: 'variant', type: String })
  _variant: SkeletonLoaderVariant = 'shape';

  @property({ attribute: 'text-height', type: String })
  textHeight: SkeletonLoaderTextHeight = 'md';

  @property({ attribute: 'line-count', type: Number })
  lineCount: number = 1;

  @property({ attribute: 'radius', type: Boolean })
  _radius: boolean = false;

  @property({ attribute: 'c11n-web-skeleton-loader', type: Boolean, reflect: true })
  readonly _c11n_web_skeleton_loader: boolean = true;

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();
  }

  appendStyles() {
    const slotStylesheet: string = slotStyles.toString()
      .replaceAll(/\bc11n-web-skeleton-loader\b/g, this.tagName.toLowerCase());
    appendStylesheet({ 
      target: this, 
      styles: slotStylesheet, 
      name: `${this.tagName.toLowerCase()}-slot`
    });
  }


  override render() {
    const skeletonLoaderHtml = html`
      <div class="c11n-skeleton-loader">
        <span class="c11n-skeleton-loader__sr-only">Loading, please wait</span>
      </div>
    `;

    return html`
      ${this._variant === 'text-block'
        ? html`
            ${Array.from({ length: this.lineCount || 1 }).map(
              () => html`${skeletonLoaderHtml}`
            )}
          `
        : html`${skeletonLoaderHtml}`}
    `;
  }
}

export const C11nSkeletonLoader = SkeletonLoaderComponent.selector;
export const C11nSkeletonLoaderLiteral = SkeletonLoaderComponent.literal;
